import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, updateCustomerProfile } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useBrand } from '../context/BrandContext';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const loadGoogleMaps = (() => {
  let pending: Promise<any> | null = null;
  return (): Promise<any> => {
    const w = window as any;
    if (w.google && w.google.maps) return Promise.resolve(w.google);
    if (pending) return pending;
    pending = new Promise((resolve, reject) => {
      const existing = document.getElementById('gmaps-jsapi') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve((window as any).google));
        existing.addEventListener('error', () => reject(new Error('Google Maps failed to load')));
        return;
      }
      if (!GOOGLE_MAPS_API_KEY) { reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY')); return; }
      const s = document.createElement('script');
      s.id = 'gmaps-jsapi';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      s.async = true; s.defer = true;
      s.onload = () => resolve((window as any).google);
      s.onerror = () => reject(new Error('Google Maps failed to load'));
      document.head.appendChild(s);
    });
    return pending;
  };
})();

// Dark-theme styling to match the account sheet.
const darkMapStyle: any[] = [
  { elementType: 'geometry', stylers: [{ color: '#0f1114' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f1114' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a8f98' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1a1d22' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9aa0a6' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#05070a' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#1a1d22' }] },
];

// ─── Inline SVG icons (no emojis) ─────────────────────
const Icon = {
  User: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
  ),
  Menu: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 11h.01" /><path d="M11 15h.01" /><path d="M16 16h.01" /><path d="m2 16 20 6-6-20A20 20 0 0 0 2 16" /><path d="M5.71 17.11a17.04 17.04 0 0 1 11.4-11.4" /></svg>
  ),
  Sushi: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7" cy="12" r="5" /><circle cx="7" cy="12" r="1.8" /><circle cx="17" cy="12" r="5" /><circle cx="17" cy="12" r="1.8" /></svg>
  ),
  Package: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>
  ),
  LogOut: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" x2="9" y1="12" y2="12" /></svg>
  ),
  MapPin: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
  ),
  Search: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
  ),
  Check: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
  ),
  X: ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
  ),
};

const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, setProfile, isAuthenticated } = useAuth();
  const { lang, setLang } = useLanguage();
  const { brand } = useBrand();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', street: '', postcode: '', city: '' });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [lat, setLat] = useState(50.8825);
  const [lng, setLng] = useState(6.8647);
  const [mapMode, setMapMode] = useState<'dark' | 'light'>(() => {
    try { return (localStorage.getItem('sushiki_map_mode') as 'dark' | 'light') || 'dark'; } catch { return 'dark'; }
  });
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const googleMap = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const getSessionToken = () => {
    try { return JSON.parse(localStorage.getItem('sushiki_session') || '{}').sessionToken || ''; } catch { return ''; }
  };

  const close = () => {
    // Behave as a popup: dismiss by going back, never stack a new page
    if (window.history.length > 1) navigate(-1);
    else navigate('/', { replace: true });
  };

  useEffect(() => {
    if (!isAuthenticated && !user) {
      navigate('/auth', { state: { from: '/account' }, replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name || '', phone: profile.phone || '',
        email: (profile as any).email || '',
        street: profile.street || '', postcode: profile.postcode || '', city: profile.city || '',
      });
    }
  }, [profile]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reverseGeocode = async (latitude: number, longitude: number) => {
    try {
      const google = await loadGoogleMaps();
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: latitude, lng: longitude }, region: 'de' },
        (results: any, status: any) => {
          if (status !== 'OK' || !results?.[0]) return;
          const comps = results[0].address_components || [];
          const get = (type: string) => comps.find((c: any) => c.types.includes(type))?.long_name || '';
          const streetName = get('route');
          const houseNum = get('street_number');
          const fullStreet = (houseNum ? `${streetName} ${houseNum}` : streetName).trim();
          const fullCity = get('locality') || get('postal_town') || get('administrative_area_level_2') || '';
          const fullPostcode = get('postal_code');
          setForm((f) => ({
            ...f,
            street: fullStreet || f.street,
            postcode: fullPostcode || f.postcode,
            city: fullCity || f.city,
          }));
          setSearchQuery(results[0].formatted_address || `${fullStreet}, ${fullPostcode} ${fullCity}`);
        },
      );
    } catch { /* non-critical */ }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const google = await loadGoogleMaps();
        const service = new google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          { input: value, componentRestrictions: { country: 'de' }, types: ['address'] },
          (preds: any, status: any) => {
            if (status !== 'OK' || !preds) { setSuggestions([]); setShowSuggestions(false); return; }
            setSuggestions(preds);
            setShowSuggestions(preds.length > 0);
          },
        );
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 400);
  };

  const selectSuggestion = async (s: any) => {
    // Google Places Autocomplete prediction — resolve to lat/lng + components via Geocoder.
    setSuggestions([]);
    setShowSuggestions(false);
    try {
      const google = await loadGoogleMaps();
      const geocoder = new google.maps.Geocoder();
      const query = s.place_id
        ? { placeId: s.place_id }
        : { address: s.description || s.structured_formatting?.main_text || '' };
      geocoder.geocode(query, (results: any, status: any) => {
        if (status !== 'OK' || !results?.[0]) return;
        const r = results[0];
        const comps = r.address_components || [];
        const get = (type: string) => comps.find((c: any) => c.types.includes(type))?.long_name || '';
        const streetName = get('route');
        const houseNum = get('street_number');
        const fullStreet = (houseNum ? `${streetName} ${houseNum}` : streetName).trim();
        const fullCity = get('locality') || get('postal_town') || get('administrative_area_level_2') || '';
        const fullPostcode = get('postal_code');
        const newLat = r.geometry?.location?.lat?.();
        const newLng = r.geometry?.location?.lng?.();

        setForm((f) => ({ ...f, street: fullStreet, postcode: fullPostcode, city: fullCity }));
        setSearchQuery(r.formatted_address || s.description || `${fullStreet}, ${fullPostcode} ${fullCity}`);

        if (typeof newLat === 'number' && typeof newLng === 'number') {
          setLat(newLat); setLng(newLng);
          if (googleMap.current && markerRef.current) {
            googleMap.current.setCenter({ lat: newLat, lng: newLng });
            googleMap.current.setZoom(17);
            markerRef.current.setPosition({ lat: newLat, lng: newLng });
          }
        }
      });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!editing || !mapContainerRef.current || googleMap.current) return;
    let cancelled = false;

    (async () => {
      let google: any;
      try {
        google = await loadGoogleMaps();
      } catch (err) {
        console.error('[AccountPage] Google Maps failed to load:', err);
        return;
      }
      if (cancelled || !mapContainerRef.current) return;

      const map = new google.maps.Map(mapContainerRef.current, {
        center: { lat, lng },
        zoom: 15,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'greedy',
        styles: mapMode === 'dark' ? darkMapStyle : [],
        backgroundColor: mapMode === 'dark' ? '#0f1114' : '#eef1f5',
      });

      const markerSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">
          <defs>
            <filter id="s" x="-50%" y="-30%" width="200%" height="200%">
              <feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="#000" flood-opacity="0.45"/>
            </filter>
          </defs>
          <g filter="url(#s)">
            <path d="M22 2 C11 2 3 10 3 21 C3 34 22 54 22 54 C22 54 41 34 41 21 C41 10 33 2 22 2 Z"
                  fill="${brand.primary}" stroke="#ffffff" stroke-width="3"/>
            <circle cx="22" cy="21" r="7" fill="#0a0a0a"/>
          </g>
        </svg>`;

      const marker = new google.maps.Marker({
        position: { lat, lng },
        map,
        draggable: true,
        icon: {
          url: `data:image/svg+xml;utf8,${encodeURIComponent(markerSvg)}`,
          scaledSize: new google.maps.Size(44, 56),
          anchor: new google.maps.Point(22, 54),
        },
        animation: google.maps.Animation.DROP,
      });

      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        if (!pos) return;
        const nl = pos.lat(); const ng = pos.lng();
        setLat(nl); setLng(ng);
        reverseGeocode(nl, ng);
      });
      map.addListener('click', (e: any) => {
        if (!e.latLng) return;
        const nl = e.latLng.lat(); const ng = e.latLng.lng();
        marker.setPosition(e.latLng);
        setLat(nl); setLng(ng);
        reverseGeocode(nl, ng);
      });

      googleMap.current = map;
      markerRef.current = marker;

      // If we have a saved address, geocode it via Google and recenter.
      if (form.street && form.postcode) {
        try {
          const geocoder = new google.maps.Geocoder();
          const fullAddress = `${form.street}, ${form.postcode} ${form.city}, Germany`;
          geocoder.geocode(
            { address: fullAddress, region: 'de', componentRestrictions: { country: 'DE' } },
            (results: any, status: any) => {
              if (cancelled || status !== 'OK' || !results?.[0]) return;
              const loc = results[0].geometry?.location;
              if (!loc) return;
              const nl = loc.lat(); const ng = loc.lng();
              setLat(nl); setLng(ng);
              map.setCenter({ lat: nl, lng: ng });
              marker.setPosition({ lat: nl, lng: ng });
            },
          );
        } catch { /* keep default */ }
      } else if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (cancelled) return;
            const gl = pos.coords.latitude; const gn = pos.coords.longitude;
            setLat(gl); setLng(gn);
            map.setCenter({ lat: gl, lng: gn });
            marker.setPosition({ lat: gl, lng: gn });
            reverseGeocode(gl, gn);
          },
          () => {},
          { enableHighAccuracy: true, timeout: 8000 },
        );
      }
    })();

    return () => {
      cancelled = true;
      googleMap.current = null;
      if (markerRef.current) { try { markerRef.current.setMap(null); } catch { /* ignore */ } }
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, brand.id]);

  // Re-style the map in place when user flips the light/dark toggle.
  useEffect(() => {
    try { localStorage.setItem('sushiki_map_mode', mapMode); } catch { /* ignore */ }
    if (googleMap.current) {
      googleMap.current.setOptions({
        styles: mapMode === 'dark' ? darkMapStyle : [],
        backgroundColor: mapMode === 'dark' ? '#0f1114' : '#eef1f5',
      });
    }
  }, [mapMode]);

  const handleSave = async () => {
    setSaving(true); setSaveSuccess(false);
    try {
      const sessionToken = getSessionToken();
      if (sessionToken) {
        await updateCustomerProfile({ sessionToken, name: form.name.trim(), street: form.street.trim(), postcode: form.postcode.trim(), city: form.city.trim(), latitude: lat, longitude: lng });
      } else if (user) {
        const { updateDoc, doc: fsDoc, serverTimestamp } = await import('firebase/firestore');
        const { db: fsDb } = await import('../services/firebase');
        await updateDoc(fsDoc(fsDb, 'customers', user.uid), { name: form.name.trim(), phone: form.phone.trim(), street: form.street.trim(), postcode: form.postcode.trim(), city: form.city.trim(), updated_at: serverTimestamp() });
      }
      setProfile({ ...profile!, name: form.name.trim(), phone: form.phone.trim(), street: form.street.trim(), postcode: form.postcode.trim(), city: form.city.trim() });
      try {
        const sess = JSON.parse(localStorage.getItem('sushiki_session') || '{}');
        if (sess.sessionToken) { sess.profile = { ...sess.profile, name: form.name.trim(), street: form.street.trim(), postcode: form.postcode.trim(), city: form.city.trim() }; localStorage.setItem('sushiki_session', JSON.stringify(sess)); window.dispatchEvent(new Event('sushiki-auth-changed')); }
      } catch {}
      setEditing(false); setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) { console.error('Save error:', err); }
    finally { setSaving(false); }
  };

  const handleLogout = () => {
    signOut(auth).catch(() => {});
    localStorage.removeItem('sushiki_session');
    localStorage.removeItem('qr_cart_v2');
    window.dispatchEvent(new Event('sushiki-auth-changed'));
    window.location.href = '/';
  };

  // ─── Styles driven by brand palette ─────────────
  const bg = brand.bg;
  const bgElev = brand.bgElev;
  const border = `${brand.text}1a`;
  const borderStrong = `${brand.text}2e`;
  const primary = brand.primary;
  const primaryDark = brand.primaryDark;
  const textMain = brand.text;
  const textMuted = brand.textMuted;

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', borderRadius: 12,
    border: `1px solid ${borderStrong}`, background: `${brand.text}08`, color: textMain,
    fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color .2s, background .2s',
  };
  const labelStyle: React.CSSProperties = {
    color: textMuted, fontSize: '.72rem', letterSpacing: '.06em',
    textTransform: 'uppercase', display: 'block', marginBottom: 6, fontWeight: 600,
  };

  const hasProfile = profile && (profile.name || profile.street);

  if (!user && !isAuthenticated) return null;

  return (
    <>
      {/* Google Fonts */}
      <link
        rel="stylesheet"
        href={
          brand.id === 'donvito'
            ? 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700&family=Inter:wght@400;500;600;700&display=swap'
            : 'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap'
        }
      />

      <style>{`
        @keyframes bxFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bxSlideUp { from { opacity: 0; transform: translateY(16px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }

        .bx-backdrop {
          position: fixed; inset: 0; z-index: 200;
          background: rgba(0,0,0,.35);
          backdrop-filter: blur(14px) saturate(160%);
          -webkit-backdrop-filter: blur(14px) saturate(160%);
          animation: bxFade .25s ease both;
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
        }
        .bx-sheet {
          position: relative;
          width: 100%;
          max-width: 560px;
          max-height: calc(100vh - 48px);
          overflow: hidden;
          background: ${bgElev}f0;
          border: 1px solid ${primary}35;
          border-radius: 24px;
          box-shadow: 0 30px 80px -10px rgba(0,0,0,.7), 0 0 0 1px ${primary}22, 0 0 60px -20px ${primary}55;
          animation: bxSlideUp .35s cubic-bezier(.22,1,.36,1) both;
          display: flex;
          flex-direction: column;
          font-family: ${brand.fontBody};
        }
        .bx-body {
          overflow-y: auto;
          padding: 20px 22px 28px;
          -webkit-overflow-scrolling: touch;
        }

        .bx-input:focus {
          border-color: ${primary} !important;
          background: ${brand.text}10 !important;
          box-shadow: 0 0 0 3px ${primary}22;
        }

        /* Mobile: full-screen sheet */
        @media (max-width: 560px) {
          .bx-backdrop { padding: 0; }
          .bx-sheet { max-width: 100%; max-height: 100vh; height: 100vh; border-radius: 0; }
        }
      `}</style>

      <div
        className="bx-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      >
        <div className="bx-sheet">
          {/* Header */}
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '18px 22px',
              borderBottom: `1px solid ${border}`,
              background: `linear-gradient(180deg, ${primary}10 0%, transparent 100%)`,
            }}
          >
            <button
              onClick={close}
              aria-label="Close"
              style={{
                width: 36, height: 36, borderRadius: 999,
                background: `${brand.text}10`,
                border: `1px solid ${border}`,
                color: textMain,
                cursor: 'pointer',
                display: 'grid', placeItems: 'center',
              }}
            >
              <Icon.X size={16} color={textMain} />
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ color: primary, fontSize: '.7rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase' }}>
                {brand.id === 'donvito' ? 'Pizza Don Vito' : 'SushiKi'}
              </div>
              <h2
                style={{
                  color: textMain, fontSize: '1.25rem', fontWeight: 800,
                  margin: '2px 0 0', letterSpacing: '-0.01em',
                  fontFamily: brand.fontDisplay,
                }}
              >
                {lang === 'de' ? 'Mein Konto' : 'My Account'}
              </h2>
            </div>
            <div
              style={{
                display: 'inline-flex', padding: 3,
                background: `${brand.text}0a`,
                border: `1px solid ${border}`,
                borderRadius: 999,
              }}
            >
              {(['de', 'en'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  style={{
                    padding: '5px 12px',
                    background: lang === l ? primary : 'transparent',
                    color: lang === l ? '#0a0a0a' : textMain,
                    border: 'none', borderRadius: 999,
                    fontSize: '.7rem', fontWeight: 700, letterSpacing: '.08em',
                    cursor: 'pointer',
                  }}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="bx-body">
            {/* Profile card */}
            <div
              style={{
                background: `${brand.text}05`,
                border: `1px solid ${border}`,
                borderRadius: 18,
                padding: '18px 20px',
                marginBottom: 18,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 12,
                      background: `${primary}1f`,
                      border: `1px solid ${primary}40`,
                      display: 'grid', placeItems: 'center',
                    }}
                  >
                    <Icon.User size={18} color={primary} />
                  </div>
                  <h3 style={{ color: textMain, fontSize: '1rem', margin: 0, fontWeight: 700 }}>
                    {lang === 'de' ? 'Profil' : 'Profile'}
                  </h3>
                </div>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    style={{
                      background: `${primary}1a`,
                      border: `1px solid ${primary}55`,
                      color: primary,
                      padding: '7px 16px', borderRadius: 10,
                      fontSize: '.8rem', fontWeight: 700, cursor: 'pointer',
                      transition: 'all .2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = `${primary}2a`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = `${primary}1a`; }}
                  >
                    {lang === 'de' ? 'Bearbeiten' : 'Edit'}
                  </button>
                )}
              </div>

              {saveSuccess && (
                <div
                  style={{
                    background: 'rgba(76,175,80,.12)',
                    border: '1px solid rgba(76,175,80,.3)',
                    borderRadius: 10,
                    padding: '10px 12px',
                    marginBottom: 14,
                    color: '#6ee06e',
                    fontSize: '.85rem', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <Icon.Check size={16} color="#6ee06e" />
                  {lang === 'de' ? 'Profil gespeichert!' : 'Profile saved!'}
                </div>
              )}

              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>{lang === 'de' ? 'Name' : 'Full Name'} *</label>
                    <input className="bx-input" type="text" value={form.name} placeholder="Max Mustermann" onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} autoFocus />
                  </div>
                  <div>
                    <label style={labelStyle}>{lang === 'de' ? 'Telefon' : 'Phone'}</label>
                    <div style={{ ...inputStyle, background: `${brand.text}04`, color: primary, cursor: 'not-allowed', fontWeight: 600 }}>
                      {form.phone || '—'}
                    </div>
                  </div>

                  <div style={{ position: 'relative' }}>
                    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon.Search size={12} color={textMuted} />
                      {lang === 'de' ? 'Adresse suchen' : 'Search address'}
                    </label>
                    <input
                      className="bx-input"
                      type="text"
                      value={searchQuery}
                      placeholder={lang === 'de' ? 'Straße, PLZ oder Stadt eingeben...' : 'Type street, postcode or city...'}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                      onBlur={() => { setTimeout(() => setShowSuggestions(false), 200); }}
                      style={inputStyle}
                      autoComplete="off"
                    />
                    {showSuggestions && suggestions.length > 0 && (
                      <div
                        style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                          background: bgElev,
                          border: `1px solid ${borderStrong}`,
                          borderRadius: 12,
                          marginTop: 4,
                          maxHeight: 240, overflowY: 'auto',
                          boxShadow: '0 20px 40px rgba(0,0,0,.5)',
                        }}
                      >
                        {suggestions.map((s, i) => (
                          <button
                            key={`${s.place_id || i}`}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left',
                              padding: '10px 14px',
                              background: 'transparent', color: textMain,
                              border: 'none',
                              borderBottom: `1px solid ${border}`,
                              cursor: 'pointer',
                              fontSize: '.85rem',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = `${primary}10`)}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            {s.description || s.structured_formatting?.main_text || ''}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>{lang === 'de' ? 'Straße & Nr.' : 'Street & No.'}</label>
                    <input className="bx-input" type="text" value={form.street} placeholder="Musterstraße 12" onChange={(e) => setForm({ ...form, street: e.target.value })} style={inputStyle} />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>{lang === 'de' ? 'PLZ' : 'Postcode'}</label>
                      <input className="bx-input" type="text" value={form.postcode} placeholder="50354" onChange={(e) => setForm({ ...form, postcode: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>{lang === 'de' ? 'Stadt' : 'City'}</label>
                      <input className="bx-input" type="text" value={form.city} placeholder="Hürth" onChange={(e) => setForm({ ...form, city: e.target.value })} style={inputStyle} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
                        <Icon.MapPin size={12} color={textMuted} />
                        {lang === 'de' ? 'Standort auf Karte' : 'Location on map'}
                      </label>
                      <div
                        role="tablist"
                        aria-label="Map theme"
                        style={{
                          display: 'inline-flex', padding: 3,
                          background: `${brand.text}0a`,
                          border: `1px solid ${border}`,
                          borderRadius: 999,
                        }}
                      >
                        {(['dark', 'light'] as const).map((m) => {
                          const active = mapMode === m;
                          const label = m === 'dark'
                            ? (lang === 'de' ? 'Dunkel' : 'Dark')
                            : (lang === 'de' ? 'Hell' : 'Light');
                          return (
                            <button
                              key={m}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              onClick={() => setMapMode(m)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '4px 10px',
                                background: active ? primary : 'transparent',
                                color: active ? '#0a0a0a' : textMain,
                                border: 'none', borderRadius: 999,
                                fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                              }}
                            >
                              <span aria-hidden style={{ lineHeight: 1 }}>{m === 'dark' ? '☾' : '☀'}</span>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div
                      ref={mapContainerRef}
                      style={{
                        width: '100%', height: 220,
                        borderRadius: 14,
                        border: `1px solid ${borderStrong}`,
                        overflow: 'hidden',
                        background: mapMode === 'dark' ? '#0a0a0a' : '#eef1f5',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                    <button
                      onClick={handleSave}
                      disabled={saving || !form.name.trim()}
                      style={{
                        flex: 2,
                        padding: '14px 0', borderRadius: 12, border: 'none',
                        background: saving ? `${brand.text}22` : primary,
                        color: saving ? textMuted : '#0a0a0a',
                        fontSize: '.95rem', fontWeight: 800, letterSpacing: '.02em',
                        cursor: saving ? 'wait' : 'pointer',
                        boxShadow: saving ? 'none' : `0 8px 24px ${primary}55`,
                        transition: 'all .2s',
                      }}
                    >
                      {saving ? '…' : (lang === 'de' ? 'Speichern' : 'Save')}
                    </button>
                    <button
                      onClick={() => { setEditing(false); }}
                      style={{
                        flex: 1,
                        padding: '14px 0', borderRadius: 12,
                        border: `1px solid ${border}`,
                        background: 'transparent', color: textMuted,
                        fontSize: '.95rem', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {lang === 'de' ? 'Abbrechen' : 'Cancel'}
                    </button>
                  </div>
                </div>
              ) : (
                hasProfile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <ProfileRow label={lang === 'de' ? 'Name' : 'Name'} value={profile?.name || '—'} color={textMuted} valueColor={textMain} />
                    <ProfileRow label={lang === 'de' ? 'Telefon' : 'Phone'} value={profile?.phone || '—'} color={textMuted} valueColor={primary} strong />
                    <ProfileRow
                      label={lang === 'de' ? 'Adresse' : 'Address'}
                      value={profile?.street ? `${profile.street}, ${profile.postcode} ${profile.city}` : '—'}
                      color={textMuted}
                      valueColor={textMain}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '18px 0' }}>
                    <p style={{ color: textMuted, fontSize: '.9rem', marginBottom: 14 }}>
                      {lang === 'de' ? 'Kein Profil vorhanden. Bitte vervollständigen Sie Ihre Daten.' : 'No profile data yet. Please complete your details.'}
                    </p>
                    <button
                      onClick={() => setEditing(true)}
                      style={{
                        background: primary, border: 'none', color: '#0a0a0a',
                        padding: '11px 24px', borderRadius: 12,
                        fontSize: '.9rem', fontWeight: 800, cursor: 'pointer',
                        boxShadow: `0 8px 20px ${primary}40`,
                      }}
                    >
                      {lang === 'de' ? 'Profil ausfüllen' : 'Complete Profile'}
                    </button>
                  </div>
                )
              )}
            </div>

            {/* Quick actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              <button
                onClick={() => navigate('/')}
                style={{
                  width: '100%',
                  padding: '14px 18px', borderRadius: 14, border: 'none',
                  background: primary,
                  color: '#0a0a0a',
                  fontSize: '.95rem', fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  boxShadow: `0 10px 24px ${primary}40`,
                  transition: 'all .2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = primaryDark; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = primary; }}
              >
                {brand.id === 'donvito' ? <Icon.Menu size={18} color="#0a0a0a" /> : <Icon.Sushi size={18} color="#0a0a0a" />}
                {lang === 'de' ? 'Speisekarte' : 'Browse Menu'}
              </button>

              <button
                onClick={() => navigate('/orders')}
                style={{
                  width: '100%',
                  padding: '14px 18px', borderRadius: 14,
                  border: `1px solid ${border}`,
                  background: 'transparent', color: textMain,
                  fontSize: '.95rem', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  transition: 'all .2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${primary}10`;
                  e.currentTarget.style.borderColor = `${primary}55`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = border;
                }}
              >
                <Icon.Package size={18} color={textMain} />
                {lang === 'de' ? 'Meine Bestellungen' : 'My Orders'}
              </button>
            </div>

            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '13px 18px', borderRadius: 14,
                border: '1px solid rgba(239,83,80,.4)',
                background: 'rgba(239,83,80,.08)', color: '#ef5350',
                fontSize: '.92rem', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                transition: 'all .2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,83,80,.16)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,83,80,.08)'; }}
            >
              <Icon.LogOut size={16} color="#ef5350" />
              {lang === 'de' ? 'Abmelden' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const ProfileRow: React.FC<{ label: string; value: string; color: string; valueColor: string; strong?: boolean }> = ({ label, value, color, valueColor, strong }) => (
  <div
    style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-start', gap: 12,
    }}
  >
    <span
      style={{
        color, fontSize: '.72rem',
        letterSpacing: '.08em', textTransform: 'uppercase',
        fontWeight: 600, paddingTop: 2,
      }}
    >
      {label}
    </span>
    <span
      style={{
        color: valueColor, fontSize: '.92rem',
        fontWeight: strong ? 700 : 500,
        textAlign: 'right', maxWidth: '65%',
        lineHeight: 1.4,
      }}
    >
      {value}
    </span>
  </div>
);

export default AccountPage;
